import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Clock, Wrench, Package, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Plus, Minus, Settings, Download, Shield, BarChart2, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

async function apiFetch(token, path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  if (res.status === 204) return null;
  return res.json();
}

const MACHINE_COLORS = {
  'LATHE':       'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'LATHE+ASSY':  'text-purple-400 bg-purple-500/10 border-purple-500/20',
  'LATHE+MILL':  'text-orange-400 bg-orange-500/10 border-orange-500/20',
};

function CapacityBar({ capacity }) {
  const pct = Math.min(capacity.utilization_pct, 120);
  const color = pct > 100 ? 'bg-red-500' : pct > capacity.near_capacity ? 'bg-yellow-500' : 'bg-green-500';
  const textColor = pct > 100 ? 'text-red-400' : pct > 95 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="bg-[#1a1a1a] border border-white/10 p-5" data-testid="capacity-bar">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-bebas text-xl text-white tracking-wider">SHOP DAY CAPACITY</h3>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">
            {capacity.machinist_count} machinist{capacity.machinist_count !== 1 ? 's' : ''} ·
            {capacity.effective_min} min available ·
            Est. done: <strong className="text-white">{capacity.completion_time}</strong>
          </p>
        </div>
        <div className="text-right">
          <div className={`font-bebas text-3xl tracking-wider ${textColor}`}>{capacity.utilization_pct}%</div>
          <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">
            {capacity.over_capacity ? 'OVER — Day 2 needed' : capacity.near_capacity ? 'NEAR CAPACITY' : 'OK'}
          </div>
        </div>
      </div>
      {/* Capacity bar */}
      <div className="h-4 bg-[#0f0f0f] border border-white/10 overflow-hidden mb-2">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${Math.min(pct / 1.2, 100)}%` }} />
      </div>
      <div className="flex items-center justify-between text-[10px] font-inter">
        <span className="text-zinc-500">0h</span>
        <span className="text-zinc-600">Target: {Math.round(capacity.effective_min / 60 * 10) / 10}h ({capacity.shift_min / 60}h × {Math.round((capacity.effective_min / capacity.shift_min) * 100)}% efficiency)</span>
        <span className="text-zinc-500">{capacity.shift_min / 60}h</span>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div>
          <span className="font-inter text-sm text-zinc-400">Total needed: </span>
          <span className="font-bebas text-lg text-white tracking-wide">{capacity.total_needed_min} min</span>
          <span className="font-inter text-xs text-zinc-600 ml-2">({Math.round(capacity.total_needed_min / 60 * 10) / 10}h)</span>
        </div>
        {!capacity.over_capacity && (
          <div className="text-right">
            <span className="font-inter text-sm text-zinc-400">Remaining: </span>
            <span className="font-bebas text-lg text-green-400 tracking-wide">{Math.round(capacity.remaining_min)} min</span>
          </div>
        )}
        {capacity.over_capacity && (
          <span className="font-inter text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-2 py-1">
            Day 2 overflow: some runs deferred
          </span>
        )}
      </div>
    </div>
  );
}

function RunCard({ run, index, dayLabel, onQtyChange, onNotesChange, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(run.user_notes || '');
  const profile = run.profile || {};
  const machining = run.machining || {};
  const machineCode = profile.machine_code || 'LATHE';
  const machineCls = MACHINE_COLORS[machineCode] || MACHINE_COLORS.LATHE;

  const isInventory = run.source === 'inventory_replenishment';

  return (
    <div data-testid={`run-card-${run.product_slug}`} className={`border transition-all ${run.nfa_item ? 'border-orange-500/30 bg-orange-500/3' : 'border-white/10 bg-[#1a1a1a]'} mb-3`}>
      {/* Run header */}
      <div className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/2 transition-colors cursor-pointer" onClick={() => setExpanded(!expanded)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setExpanded(!expanded)}>
        {/* Number */}
        <div className="w-8 h-8 bg-[#0f0f0f] border border-white/10 flex items-center justify-center flex-shrink-0">
          <span className="font-bebas text-sm text-zinc-500 tracking-wide">{String(index).padStart(2,'0')}</span>
        </div>

        {/* Product + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bebas text-base text-white tracking-wide">{run.product_name}</span>
            {run.nfa_item && <span className="flex items-center gap-1 font-inter text-[9px] font-bold text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5"><Shield size={8} />NFA</span>}
            {isInventory && <span className="font-inter text-[9px] font-bold text-cyan-400 border border-cyan-500/20 bg-cyan-500/10 px-1.5 py-0.5 uppercase">INVENTORY</span>}
            <span className={`font-inter text-[9px] font-bold px-1.5 py-0.5 border ${machineCls}`}>{machineCode}</span>
          </div>
          <div className="flex items-center gap-4 mt-0.5">
            <span className="font-inter text-[11px] text-zinc-500">{profile.material_spec} ({profile.material_diameter_in}"Ø)</span>
            <span className="font-inter text-[11px] text-zinc-600">·</span>
            <span className="font-inter text-[11px] text-zinc-500">{machining.material_length_ft} ft bar stock</span>
          </div>
        </div>

        {/* Time breakdown */}
        <div className="flex items-center gap-4 flex-shrink-0 text-right">
          <div className="hidden sm:block text-right">
            <div className="font-inter text-[10px] text-zinc-600 uppercase tracking-wider">Setup</div>
            <div className="font-inter text-sm text-zinc-400">{machining.setup_min}m</div>
          </div>
          <div className="hidden sm:block text-right">
            <div className="font-inter text-[10px] text-zinc-600 uppercase tracking-wider">Run</div>
            <div className="font-inter text-sm text-zinc-400">{machining.cycle_total_min}m</div>
          </div>
          <div className="text-right">
            <div className="font-inter text-[10px] text-zinc-600 uppercase tracking-wider">Total</div>
            <div className="font-bebas text-xl text-white tracking-wide">{machining.total_min}m</div>
          </div>
          {/* Qty control — allow 0 which triggers removal confirmation */}
          <div className="flex items-center border border-white/20 ml-2">
            <button onClick={(e) => { e.stopPropagation(); onQtyChange(run.product_slug, run.total_quantity - 1); }}
              title={run.total_quantity <= 1 ? 'Click to remove this run from the cut sheet' : 'Decrease quantity'}
              className={`px-2 py-1.5 hover:bg-white/5 transition-colors text-xs ${run.total_quantity <= 1 ? 'text-red-400 hover:text-red-300' : 'text-zinc-500 hover:text-white'}`}>
              {run.total_quantity <= 1 ? <Trash2 size={12} /> : '−'}
            </button>
            <span className="px-2 py-1.5 font-bebas text-base text-white border-x border-white/20">{run.total_quantity}</span>
            <button onClick={(e) => { e.stopPropagation(); onQtyChange(run.product_slug, run.total_quantity + 1); }}
              className="px-2 py-1.5 text-zinc-500 hover:text-white hover:bg-white/5 transition-colors text-xs">+</button>
          </div>
          {expanded ? <ChevronUp size={14} className="text-zinc-400 ml-2" /> : <ChevronDown size={14} className="text-zinc-400 ml-2" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-white/10 p-5 space-y-4 bg-[#141414]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Operations */}
            <div>
              <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Machining Operations</p>
              <div className="space-y-1.5">
                {profile.operations?.map((op, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="font-inter text-[10px] text-orange-500 mt-0.5 flex-shrink-0">{String(i + 1).padStart(2,'0')}</span>
                    <span className={`font-inter text-xs ${op.startsWith('VERIFY') || op.startsWith('CRITICAL') || op.startsWith('NFA') ? 'text-orange-300 font-semibold' : 'text-zinc-400'}`}>{op}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Material + timing + QC */}
            <div className="space-y-3">
              {/* Material card */}
              <div className="bg-[#1e1e1e] border border-white/5 p-3">
                <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Material Required</p>
                <div className="font-bebas text-2xl text-white tracking-wide">{machining.material_length_ft} ft</div>
                <div className="font-inter text-xs text-zinc-400">{profile.material_diameter_in}" OD × {machining.material_length_in}" total</div>
                <div className="font-inter text-xs text-zinc-500 mt-1">{profile.material_spec}</div>
                {profile.material_notes && (
                  <div className="font-inter text-[10px] text-yellow-400 mt-2 border-t border-white/5 pt-1">{profile.material_notes}</div>
                )}
              </div>

              {/* Time breakdown */}
              <div className="bg-[#1e1e1e] border border-white/5 p-3">
                <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Time Estimate</p>
                <div className="space-y-1">
                  <div className="flex justify-between font-inter text-xs">
                    <span className="text-zinc-500">Setup (one-time):</span>
                    <span className="text-white">{machining.setup_min} min</span>
                  </div>
                  <div className="flex justify-between font-inter text-xs">
                    <span className="text-zinc-500">{machining.cycle_per_unit_min} min × {run.total_quantity} units:</span>
                    <span className="text-white">{machining.cycle_total_min} min</span>
                  </div>
                  <div className="flex justify-between font-inter text-sm font-semibold border-t border-white/5 pt-1 mt-1">
                    <span className="text-zinc-300">Total for this run:</span>
                    <span className="text-orange-400">{machining.total_min} min ({machining.total_hr}h)</span>
                  </div>
                </div>
              </div>

              {/* Quality checks */}
              {profile.quality_checks?.length > 0 && (
                <div className="bg-[#1e1e1e] border border-white/5 p-3">
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Quality Checks</p>
                  {profile.quality_checks.map((qc, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">☐</span>
                      <span className="font-inter text-[11px] text-zinc-400">{qc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Setup notes */}
          {profile.setup_notes && (
            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-2.5">
              <span className="font-inter text-[10px] font-bold text-blue-400 uppercase tracking-wider">Shop Note: </span>
              <span className="font-inter text-xs text-blue-300">{profile.setup_notes}</span>
            </div>
          )}
          {profile.nfa_note && (
            <div className="bg-orange-500/10 border border-orange-500/30 px-4 py-2.5">
              <span className="font-inter text-[10px] font-bold text-orange-400 uppercase tracking-wider">⚠ NFA: </span>
              <span className="font-inter text-xs text-orange-300">{profile.nfa_note}</span>
            </div>
          )}

          {/* Notes & Remove */}
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Machinist Notes (printed on cut sheet)</label>
              <textarea
                rows={2}
                value={notes}
                onChange={e => { setNotes(e.target.value); onNotesChange(run.product_slug, e.target.value); }}
                className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-xs px-3 py-2 outline-none transition-colors resize-none placeholder-zinc-600"
                placeholder="Add notes for the machinist..."
              />
            </div>
            <button onClick={() => onQtyChange(run.product_slug, 0)}
              className="font-inter text-xs text-red-400 border border-red-500/20 hover:bg-red-500/10 px-3 py-2 mt-5 transition-colors whitespace-nowrap flex items-center gap-1.5">
              <Trash2 size={12} />Remove from Cut Sheet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminProductionPlanner() {
  const { planId } = useParams();
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [overrides, setOverrides] = useState({});
  const [shopSettingsOpen, setShopSettingsOpen] = useState(false);
  const [shopSettings, setShopSettings] = useState(null);
  const [addingSuggestion, setAddingSuggestion] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState('');

  const load = useCallback(async (currentOverrides) => {
    setLoading(true);
    setError('');
    try {
      // Always pass the overrides explicitly — avoids stale closure issues
      const data = await apiFetch(token, `/api/admin/machining/plans/${planId}/enrich`, {
        method: 'POST',
        body: JSON.stringify({ overrides: currentOverrides ?? overrides }),
      });
      setPlanData(data);
      if (!shopSettings) {
        const s = await apiFetch(token, '/api/admin/machining/settings');
        setShopSettings(s);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [planId, token, overrides, shopSettings]);

  useEffect(() => { load({}); }, [planId, token]);

  const handleQtyChange = (sku, newQty) => {
    // Allow going to 0 which removes the run
    if (newQty < 0) return;
    if (newQty === 0) {
      // Removing the run — confirm with user
      const run = planData?.enriched_runs?.find(r => r.product_slug === sku);
      const isOrder = run?.source !== 'inventory_replenishment';
      if (window.confirm(
        `Remove "${run?.product_name || sku}" from this cut sheet?\n\n` +
        (isOrder ? 'The linked order(s) will be returned to the Waiting for Cut Sheet list.' : 'This inventory run will be removed from today\'s plan.')
      )) {
        handleRemoveRun(sku);
      }
      return;
    }
    const newOverrides = { ...overrides, [sku]: { ...(overrides[sku] || {}), quantity: newQty } };
    setOverrides(newOverrides);
    // Re-enrich with updated qty — we'll need to update the local state manually for now
    setPlanData(prev => {
      if (!prev) return prev;
      const updateRuns = (runs) => runs.map(r => {
        if (r.product_slug !== sku) return r;
        const setup = r.profile?.setup_time_min ?? r.machining?.setup_min ?? 20;
        const cycle = r.profile?.cycle_time_per_unit_min ?? r.machining?.cycle_per_unit_min ?? 15;
        const matLen = (r.profile?.material_length_per_unit_in ?? 4) * newQty;
        return {
          ...r, total_quantity: newQty,
          machining: { setup_min: setup, cycle_per_unit_min: cycle, cycle_total_min: round1(cycle * newQty),
            total_min: round1(setup + cycle * newQty), total_hr: round2((setup + cycle * newQty) / 60),
            material_length_in: round1(matLen), material_length_ft: round2(matLen / 12) }
        };
      });
      const enriched = updateRuns(prev.enriched_runs);
      const totalNeeded = enriched.reduce((s, r) => s + r.machining.total_min, 0);
      const available = prev.capacity?.total_available_min ?? 408;
      const utilPct = round1((totalNeeded / available) * 100);
      return {
        ...prev,
        enriched_runs: enriched,
        day1_runs: enriched.filter(r => !prev.has_overflow),
        capacity: { ...prev.capacity, total_needed_min: round1(totalNeeded),
          utilization_pct: utilPct, over_capacity: utilPct > 100,
          remaining_min: round1(Math.max(0, available - totalNeeded)) }
      };
    });
  };

  const round1 = (n) => Math.round(n * 10) / 10;
  const round2 = (n) => Math.round(n * 100) / 100;

  const handleNotesChange = (sku, notes) => {
    setOverrides(prev => ({ ...prev, [sku]: { ...(prev[sku] || {}), notes } }));
  };

  const handleRemoveRun = (sku) => {
    setPlanData(prev => {
      if (!prev) return prev;
      const filtered = prev.enriched_runs.filter(r => r.product_slug !== sku);
      const totalNeeded = filtered.reduce((s, r) => s + (r.machining?.total_min || 0), 0);
      const available = prev.capacity?.total_available_min ?? 408;
      const utilPct = round1((totalNeeded / available) * 100);
      return { ...prev, enriched_runs: filtered, day1_runs: filtered,
        capacity: { ...prev.capacity, total_needed_min: round1(totalNeeded), utilization_pct: utilPct,
          remaining_min: round1(Math.max(0, available - totalNeeded)) } };
    });
  };

  const handleSaveAndReturn = async () => {
    setSaving(true);
    setError('');
    try {
      // Save the current enriched runs back to the plan in MongoDB
      const runsToSave = (planData?.enriched_runs || []).map(r => ({
        product_slug: r.product_slug,
        product_name: r.product_name,
        category: r.category,
        nfa_item: r.nfa_item,
        total_quantity: r.total_quantity,
        order_count: r.order_count,
        source: r.source || 'order',
        queue_id: r.queue_id || null,
        orders: r.orders || [],
        unit_price: r.unit_price || 0,
      }));
      await apiFetch(token, `/api/admin/production/plans/${planId}/runs`, {
        method: 'PUT',
        body: JSON.stringify({ runs: runsToSave }),
      });
      navigate('/admin/production');
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSuggestion = async (suggestion) => {
    setAddingSuggestion(suggestion.sku);
    try {
      await apiFetch(token, '/api/admin/inventory/build-queue', {
        method: 'POST',
        body: JSON.stringify({ sku: suggestion.sku, quantity: suggestion.suggested_qty, reason: suggestion.reason }),
      });
      // Reload enrichment — the build queue item will now appear in today's production runs
      await load(overrides);
    } catch (e) { alert(e.message); }
    setAddingSuggestion('');
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const updated = await apiFetch(token, '/api/admin/machining/settings', { method: 'PUT', body: JSON.stringify(shopSettings) });
      setShopSettings(updated);
      setShopSettingsOpen(false);
      await load(overrides);
    } catch (e) { alert(e.message); }
  };

  const handleDownloadPdf = async (mode) => {
    const latest = planId;
    setPdfDownloading(mode);
    try {
      const res = await fetch(`${BASE}/api/admin/production/plans/${latest}/pdf?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `WM_CutSheet_${planId}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setPdfDownloading('');
  };

  if (loading && !planData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="font-inter text-sm text-zinc-500">Enriching cut sheet with machining data...</span>
        </div>
      </div>
    );
  }

  if (error && !planData) {
    return (
      <div className="text-center py-12">
        <p className="font-inter text-sm text-red-400">{error}</p>
        <Link to="/admin/production" className="font-inter text-xs text-orange-500 mt-2 block">← Back to Production</Link>
      </div>
    );
  }

  const capacity = planData?.capacity || {};
  const day1 = planData?.day1_runs || planData?.enriched_runs || [];
  const day2 = planData?.day2_runs || [];
  const materials = planData?.material_summary || [];
  const suggestions = planData?.inventory_suggestions || [];
  const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors";

  return (
    <div data-testid="production-planner-page">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        {/* LEFT: Primary return action */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveAndReturn}
            disabled={saving}
            data-testid="save-and-return"
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest px-5 py-3 transition-colors"
          >
            <Save size={16} className={saving ? 'animate-pulse' : ''} />
            {saving ? 'SAVING...' : '← SAVE & RETURN TO PRODUCTION'}
          </button>
          <div>
            <div className="font-bebas text-xl text-white tracking-wide">{planId}</div>
            <p className="font-inter text-xs text-zinc-500">Adjust quantities and capacity, then return to generate the final PDF.</p>
          </div>
        </div>
        {/* RIGHT: Utility controls only */}
        <div className="flex items-center gap-2">
          <button onClick={() => setShopSettingsOpen(!shopSettingsOpen)}
            className="flex items-center gap-1.5 font-inter text-xs border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white px-3 py-2 transition-colors">
            <Settings size={13} />Shop Settings
          </button>
          <button onClick={() => load(overrides)} disabled={loading}
            className="flex items-center gap-1.5 font-inter text-xs border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white px-3 py-2 transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Recalculating...' : 'Recalculate'}
          </button>
        </div>
      </div>

      {/* Shop Settings panel */}
      {shopSettingsOpen && shopSettings && (
        <form onSubmit={handleSaveSettings} className="bg-[#1a1a1a] border border-white/10 p-5 mb-5">
          <h3 className="font-bebas text-lg text-white tracking-wider mb-4">SHOP SETTINGS</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { key: 'shift_hours', label: 'Shift Hours', type: 'number', min: 4, max: 16 },
              { key: 'shift_start_hour', label: 'Start Hour (24h)', type: 'number', min: 0, max: 23 },
              { key: 'efficiency_pct', label: 'Efficiency %', type: 'number', min: 50, max: 100 },
              { key: 'machinist_count', label: 'Machinists', type: 'number', min: 1, max: 10 },
            ].map(f => (
              <div key={f.key}>
                <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{f.label}</label>
                <input type={f.type} min={f.min} max={f.max} value={shopSettings[f.key] || ''} onChange={e => setShopSettings(s => ({...s, [f.key]: +e.target.value}))}
                  className={inputCls + " w-full"} />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button type="submit" className="font-inter text-sm bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 transition-colors">Save & Recalculate</button>
            <button type="button" onClick={() => setShopSettingsOpen(false)} className="font-inter text-sm border border-white/10 text-zinc-400 px-4 py-2 transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* Capacity bar */}
      {capacity && <div className="mb-5"><CapacityBar capacity={capacity} /></div>}

      {/* Day 1 Runs */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-6 h-6 bg-green-500/20 flex items-center justify-center"><span className="font-bebas text-sm text-green-400">1</span></div>
          <span className="font-bebas text-lg text-green-400 tracking-wider">TODAY'S PRODUCTION — {day1.length} RUNS</span>
          <span className="font-inter text-xs text-zinc-500">{capacity.total_needed_min} min total</span>
        </div>
        {day1.map((run, i) => (
          <RunCard key={run.product_slug} run={run} index={i + 1} dayLabel="Day 1"
            onQtyChange={handleQtyChange} onNotesChange={handleNotesChange} onRemove={handleRemoveRun} />
        ))}
      </div>

      {/* Day 2 overflow */}
      {day2.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-6 h-6 bg-orange-500/20 flex items-center justify-center"><span className="font-bebas text-sm text-orange-400">2</span></div>
            <span className="font-bebas text-lg text-orange-400 tracking-wider">DEFERRED TO DAY 2 — {day2.length} RUNS</span>
            <span className="font-inter text-xs text-zinc-500">Over capacity — schedule for tomorrow</span>
          </div>
          {day2.map((run, i) => (
            <RunCard key={run.product_slug} run={run} index={i + 1} dayLabel="Day 2"
              onQtyChange={handleQtyChange} onNotesChange={handleNotesChange} onRemove={handleRemoveRun} />
          ))}
        </div>
      )}

      {/* Material Pull List — before inventory suggestions so it's visible */}
      {materials.length > 0 && (
        <div className="bg-[#1a1a1a] border border-white/10 p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <Package size={16} className="text-orange-500" />
            <h3 className="font-bebas text-xl text-white tracking-wider">MATERIAL PULL LIST</h3>
            <span className="font-inter text-xs text-zinc-500 ml-1">— Pull this stock before machining begins</span>
          </div>
          <div className="space-y-2">
            {materials.map(m => (
              <div key={m.spec_key} className="flex items-center justify-between bg-[#141414] border border-white/5 px-5 py-3">
                <span className="font-inter text-sm text-white font-semibold">{m.spec_key}</span>
                <div className="text-right">
                  <span className="font-bebas text-2xl text-orange-500 tracking-wide">{m.total_feet} ft</span>
                  <span className="font-inter text-xs text-zinc-500 ml-2">({m.total_inches}")</span>
                  <div className="font-inter text-[10px] text-zinc-600 mt-0.5">{m.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inventory Evaluation */}
      {capacity.remaining_min > 30 && suggestions.length > 0 && (
        <div className="bg-[#1a1a1a] border border-cyan-500/20 p-5 mb-5" data-testid="inventory-suggestions">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bebas text-lg text-white tracking-wider flex items-center gap-2">
                <BarChart2 size={16} className="text-cyan-400" />
                CAPACITY AVAILABLE — EVALUATE INVENTORY?
              </h3>
              <p className="font-inter text-xs text-zinc-500 mt-0.5">
                {Math.round(capacity.remaining_min)} min remaining today. These low-stock items could fill that time:
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {suggestions.map(s => (
              <div key={s.sku} className="flex items-center justify-between bg-[#141414] border border-white/5 px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-inter text-sm font-semibold text-white">{s.product_name}</span>
                    {s.nfa_item && <Shield size={11} className="text-orange-400" />}
                    <span className="font-inter text-[9px] text-red-400 border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 uppercase">{s.stock_status.replace('_',' ')}</span>
                  </div>
                  <p className="font-inter text-xs text-zinc-500">{s.reason}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-bebas text-lg text-cyan-400">{s.suggested_qty} units</div>
                    <div className="font-inter text-[10px] text-zinc-600">~{s.est_time_min} min</div>
                  </div>
                  <button
                    data-testid={`add-suggestion-${s.sku}`}
                    onClick={() => handleAddSuggestion(s)}
                    disabled={addingSuggestion === s.sku}
                    className="flex items-center gap-1.5 font-inter text-xs bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 px-3 py-2 transition-colors disabled:opacity-40"
                  >
                    <Plus size={12} />{addingSuggestion === s.sku ? '...' : 'Add to Plan'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 text-center">
            <Link to="/admin/inventory" className="font-inter text-xs text-zinc-500 hover:text-cyan-400 transition-colors">
              View full inventory status →
            </Link>
          </div>
        </div>
      )}

      <div className="bg-[#1a1a1a] border border-orange-500/20 p-5 flex items-center justify-between">
        <div>
          <p className="font-inter text-sm text-white font-medium">Done reviewing?</p>
          <p className="font-inter text-xs text-zinc-500">Changes to quantities and removed items will be saved back to the cut sheet.</p>
        </div>
        <button
          onClick={handleSaveAndReturn}
          disabled={saving}
          data-testid="return-to-production-bottom"
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest px-6 py-3 transition-colors"
        >
          <Save size={16} className={saving ? 'animate-pulse' : ''} />
          {saving ? 'SAVING...' : '← SAVE & RETURN TO PRODUCTION'}
        </button>
      </div>
    </div>
  );
}
